import React, { useState } from "react";
import { Box, Divider, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import {
  WfIconContentCheckRegular,
  WfIconNavigationCloseRegular,
  WfIconSportEquipmentSheetOfPaperOrNotebook,
} from "@WahooFitness/redesignr";
import { RevenueCatCustomerInfo, SubscriptionType } from "@WahooFitness/cloud-client-types";

interface Props {
  userSubscription?: SubscriptionType | null;
  customerInfo?: RevenueCatCustomerInfo | null;
  onClose?: () => void;
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  const { palette } = useTheme();
  return (
    <Typography
      variant="ui-xs-bold"
      sx={{ display: "block", color: palette.warning.main, mt: 0.5, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

function JsonBlock({ data }: { data: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        fontSize: "10px",
        overflowX: "auto",
        m: 0,
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 0.5,
        lineHeight: 1.4,
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      {JSON.stringify(data ?? null, null, 2)}
    </Box>
  );
}

function SubscriptionDebugInfo({ userSubscription, customerInfo, onClose }: Props) {
  const { palette } = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = JSON.stringify(
      { subscription: userSubscription ?? null, revenueCat: customerInfo ?? null },
      null,
      2
    );
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          borderBottom: `1px dashed ${palette.warning.main}`,
        }}
      >
        <Typography variant="ui-xs-bold" sx={{ color: palette.warning.main }}>
          Debug Info
        </Typography>
        <Box display="flex" gap={0.5}>
          <Tooltip title={copied ? "Copied!" : "Copy all"}>
            <IconButton size="medium" onClick={handleCopy} sx={{ p: 0.5 }}>
              {copied ? (
                <WfIconContentCheckRegular fontSize="small" />
              ) : (
                <WfIconSportEquipmentSheetOfPaperOrNotebook fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ p: 0.5 }}>
              <WfIconNavigationCloseRegular fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <SectionHeader>Subscription</SectionHeader>
        <JsonBlock data={userSubscription} />
        <Divider sx={{ my: 0.75 }} />
        <SectionHeader>RevenueCat</SectionHeader>
        <JsonBlock data={customerInfo} />
      </Box>
    </>
  );
}

export default SubscriptionDebugInfo;
